import type { PairVerdict, PairWatchSnapshot } from "@/types/api";

import { PoissonHistogram } from "./PoissonHistogram";
import { VerdictChip } from "./VerdictChip";

/**
 * THRESHOLD tab — detailed decision breakdown for a clicked grid cell.
 * The p-value thresholds (alpha=0.005 etc.) MUST be visible — PK explicit.
 * This view does NOT compute statistics (design doc §6.5) — it reads the
 * verdict already computed by the metric_pair_watch job. The Poisson
 * histogram is a pure visualisation, it never changes the verdict.
 * Ported from products/x2mail-admin/src/views/ThresholdView.tsx, adapted to
 * the generated client's camelCase field names.
 */
export interface ThresholdViewProps {
  snapshot: PairWatchSnapshot;
  onClose: () => void;
}

// alpha thresholds per design doc §2.3, §4
const ALPHA_GLOBAL = 0.005;
const ALPHA_COUNTRY = 0.001;

function muMin(alpha: number): number {
  return -Math.log(alpha);
}

export function ThresholdView({ snapshot, onClose }: ThresholdViewProps) {
  const isCountry = snapshot.country !== null;
  const alpha = isCountry ? ALPHA_COUNTRY : ALPHA_GLOBAL;
  const muMinVal = muMin(alpha);

  const brandLabel = snapshot.brand ?? "Cała rodzina (global)";
  const countryLabel = snapshot.country ?? "Global (cross-country)";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-base font-semibold text-zinc-100">
            Transparentność progu decyzyjnego
          </h2>
          <p className="mt-0.5 text-xs text-zinc-500">
            Para: <code className="rounded bg-zinc-800 px-1">{snapshot.pairId}</code>
            {" · "}Marka: <strong className="text-zinc-300">{brandLabel}</strong>
            {" · "}Kraj: <strong className="text-zinc-300">{countryLabel}</strong>
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded px-2 py-1 text-xs text-zinc-400 hover:bg-zinc-800 transition-colors"
        >
          ✕ Zamknij
        </button>
      </div>

      {/* Werdykt */}
      <div className="flex items-center gap-3 rounded-lg border border-zinc-800 bg-zinc-900 px-4 py-3">
        <VerdictChip verdict={snapshot.verdict as PairVerdict} />
        <span className="text-sm text-zinc-300">
          {verdictExplanation(snapshot, alpha, muMinVal)}
        </span>
      </div>

      {/* Wejścia — tabela danych */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <DataCard label="L — driver (sesje)" value={snapshot.l} mono />
        <DataCard label="G — follower (generacje)" value={snapshot.g} mono />
        <DataCard label="Okno obserwacji" value={`${snapshot.window}h`} mono />
        <DataCard label="Poziom kraty baseline" value={snapshot.baselineLevel} mono />
      </div>

      {/* Parametry statystyczne */}
      <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-4">
        <h3 className="mb-3 text-sm font-semibold text-zinc-300">Parametry statystyczne</h3>
        <dl className="grid grid-cols-2 gap-x-8 gap-y-3 sm:grid-cols-3">
          <StatRow
            label="λ (ratio baseline)"
            value={snapshot.lam.toFixed(4)}
            hint="Σ_follower / Σ_driver z trailing 28 dni"
          />
          <StatRow
            label="μ = λ·L (oczekiwana)"
            value={snapshot.mu.toFixed(3)}
            hint="Oczekiwana liczba generacji przy L sesjach"
          />
          <StatRow
            label="μ_min = −ln(α)"
            value={muMinVal.toFixed(3)}
            hint={`α=${alpha} → μ_min=${muMinVal.toFixed(3)}. Poniżej progu → BELOW_VOLUME`}
            highlight={snapshot.mu < muMinVal && snapshot.verdict === "BELOW_VOLUME"}
          />
          <StatRow
            label="p_low = P(X ≤ G)"
            value={snapshot.pLow.toFixed(6)}
            hint={`Ogon dolny Poissona. Alert gdy p_low < α=${alpha}`}
            highlight={snapshot.pLow < alpha}
          />
          <StatRow
            label="α (próg p-value)"
            value={String(alpha)}
            hint={
              isCountry
                ? "α_low_country=0.001 (per-kraj, wyższy próg — kontrola multiple-comparisons)"
                : "α_low=0.005 (global, cross-country)"
            }
          />
          <StatRow
            label="G_crit (próg alertu)"
            value={snapshot.gCrit >= 0 ? String(snapshot.gCrit) : "−1 (brak progu)"}
            hint={
              snapshot.gCrit >= 0
                ? `Alert odpali się gdy generacje spadną do lub poniżej ${snapshot.gCrit}`
                : "μ < μ_min — żadna obserwacja nie osiągnie istotności"
            }
            highlight={snapshot.gCrit >= 0 && snapshot.g <= snapshot.gCrit}
          />
        </dl>
      </div>

      {/* Zdanie dla PK o G_crit — §6.4 */}
      {snapshot.gCrit >= 0 && (
        <div className="rounded-lg border border-indigo-900 bg-indigo-950/50 px-4 py-3">
          <p className="text-sm text-indigo-200">
            Przy obecnych <strong>L={snapshot.l}</strong> sesjach alert odpali się, gdy generacje
            spadną poniżej{" "}
            <strong className="text-indigo-100 text-base">{snapshot.gCrit + 1}</strong> (G ≤ G_crit=
            {snapshot.gCrit}).
          </p>
          <p className="mt-1 text-xs text-indigo-400">
            Obserwowane G={snapshot.g} ·{" "}
            {snapshot.g <= snapshot.gCrit
              ? "G jest w strefie alertu (G ≤ G_crit)"
              : "G jest powyżej progu — OK"}
          </p>
        </div>
      )}

      {/* Mini-histogram Poissona */}
      {snapshot.mu > 0 && (
        <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-4">
          <h3 className="mb-3 text-sm font-semibold text-zinc-300">
            Rozkład Poissona — wizualizacja
          </h3>
          <PoissonHistogram mu={snapshot.mu} gObs={snapshot.g} gCrit={snapshot.gCrit} />
        </div>
      )}
    </div>
  );
}

function verdictExplanation(s: PairWatchSnapshot, alpha: number, muMinVal: number): string {
  switch (s.verdict as PairVerdict) {
    case "OK":
      return `G=${s.g} > G_crit=${s.gCrit}; p_low=${s.pLow.toFixed(4)} ≥ α=${alpha}; μ=${s.mu.toFixed(2)} ≥ μ_min=${muMinVal.toFixed(2)}. Follower podąża za driverem.`;
    case "ALERT_LOW":
      return `p_low=${s.pLow.toFixed(6)} < α=${alpha} ORAZ μ=${s.mu.toFixed(2)} ≥ μ_min=${muMinVal.toFixed(2)}. Follower NIE podąża za driverem — alert uzasadniony.`;
    case "ALERT_HIGH":
      return "Ogon górny (spike). Sprawdź, czy nie ma anomalii wolumenu.";
    case "BELOW_VOLUME":
      return `μ=${s.mu.toFixed(2)} < μ_min=${muMinVal.toFixed(2)} (α=${alpha}) — za mało danych, żeby wiedzieć. To NIE jest "OK".`;
    case "NO_BASELINE":
      return "Brak węzła kraty z wystarczającym wsparciem. Zbieram baseline (~7-14 dni od pierwszego deployu).";
    default:
      // Backend keeps `verdict` a plain string (see PairWatchSnapshot in
      // backend/voice2mail_proxy/src/api/vigil.py) so it never 500s on a
      // value this UI hasn't been updated to recognise yet — mirror that
      // honesty here instead of crashing on an unknown value.
      return `Nieznany werdykt: ${s.verdict}`;
  }
}

interface DataCardProps {
  label: string;
  value: string | number;
  mono?: boolean;
}

function DataCard({ label, value, mono = false }: DataCardProps) {
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-3">
      <p className="text-xs text-zinc-500">{label}</p>
      <p
        className={["mt-1 text-lg font-semibold text-zinc-100", mono ? "font-mono" : ""].join(" ")}
      >
        {value}
      </p>
    </div>
  );
}

interface StatRowProps {
  label: string;
  value: string;
  hint: string;
  highlight?: boolean;
}

function StatRow({ label, value, hint, highlight = false }: StatRowProps) {
  return (
    <div>
      <dt className="text-xs text-zinc-500">{label}</dt>
      <dd
        className={[
          "mt-0.5 font-mono text-sm font-semibold",
          highlight ? "text-red-400" : "text-zinc-100",
        ].join(" ")}
      >
        {value}
      </dd>
      <p className="mt-0.5 text-xs text-zinc-600">{hint}</p>
    </div>
  );
}
